// Auth gate — the pure decision behind "can this authenticated user use the
// portal, and if not, WHY not".
//
// Context (Issues/2026-08-19_Api-Outage-Renders-As-Access-Denied): MSP
// entitlement is delivered ONLY by GET /api/me. When that call failed for ANY
// reason it collapsed to null, and a token-claims fallback then DENIED exactly
// the non-admin MSP population while letting global admins straight in — so an
// API outage rendered as "Access Denied", a confidently-wrong diagnosis whose
// on-screen remedy (join SG365_Blue365Signal_Portal_Admins) is a real
// privilege-escalation path.
//
// The fix is to distinguish "we could not ask" from "we asked and you are not
// entitled", and make ONLY the second Access Denied. These two functions are
// the whole decision, kept pure so they can be covered by
// scripts/authGate.test.mjs (the project has no frontend test runner).

/**
 * Classify the outcome of the /api/me fetch into one of three states.
 *
 *   'ok'           — HTTP 200 with a parseable body; an authoritative answer.
 *   'unauthorized' — the auth layer actively rejected us (HTTP 401/403).
 *   'unreachable'  — we could not get an authoritative answer: no token,
 *                    a network/CORS failure, a timeout, a 5xx, or any other
 *                    non-2xx (a 404/503 during an FC1 deploy stall lands here),
 *                    or a 200 whose body would not parse.
 *
 * @param {object} o
 * @param {boolean} [o.noToken]      getApiToken() returned null (could not even ask).
 * @param {boolean} [o.networkError] fetch threw (network/CORS/timeout).
 * @param {number|null} [o.httpStatus] the HTTP status, when a response was received.
 * @param {boolean} [o.parseError]   the 200 body failed to parse as JSON.
 * @returns {'ok'|'unauthorized'|'unreachable'}
 */
export function classifyMeStatus({ noToken = false, networkError = false, httpStatus = null, parseError = false } = {}) {
  if (noToken) return 'unreachable';
  if (networkError) return 'unreachable';
  if (httpStatus === 200) return parseError ? 'unreachable' : 'ok';
  if (httpStatus === 401 || httpStatus === 403) return 'unauthorized';
  // Everything else — 5xx, 502/503/504, 429, an unexpected 404 while routes are
  // not yet registered on a stalled host — is "we could not get an answer".
  return 'unreachable';
}

/**
 * Decide which screen an authenticated user sees.
 *
 *   'app'         — render the portal.
 *   'denied'      — Access Denied: we asked and the user is genuinely not entitled.
 *   'unreachable' — we could not ask; show an honest "can't reach the service,
 *                   retry" screen. NEVER Access Denied, NEVER a degraded shell.
 *
 * Dev mode is always 'app' (local development must not be blocked by /api/me).
 * There is deliberately NO token-claims fallback: during an outage the API
 * serves every page's data anyway, so a degraded shell is useless — an honest
 * retry screen for everyone is strictly better than denying MSP members and
 * waving admins through.
 *
 * @param {object} o
 * @param {boolean} o.isDev
 * @param {'ok'|'unauthorized'|'unreachable'} o.meStatus
 * @param {boolean} o.isGlobalAdmin   from /api/me (only meaningful when meStatus==='ok').
 * @param {number}  o.mspCodesCount   from /api/me (only meaningful when meStatus==='ok').
 * @returns {'app'|'denied'|'unreachable'}
 */
export function resolveAuthGate({ isDev = false, meStatus = 'unreachable', isGlobalAdmin = false, mspCodesCount = 0 } = {}) {
  if (isDev) return 'app';
  if (meStatus === 'unreachable') return 'unreachable';
  if (meStatus === 'unauthorized') return 'denied';
  // meStatus === 'ok': the authoritative answer.
  return (isGlobalAdmin || mspCodesCount > 0) ? 'app' : 'denied';
}
